import React, { useState, useEffect } from 'react';
import { X, Info, Tag as TagIcon, AlertTriangle } from 'lucide-react';
import { useUpdateNote, useDeleteNote, type Note, type NoteCategory, type NotePriority } from '../../../services/queries/noteQueries';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../../standard';

interface EditNoteModalProps {
  note: Note | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditNoteModal: React.FC<EditNoteModalProps> = ({ note, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general' as NoteCategory,
    priority: 'medium' as NotePriority,
    tags: [] as string[],
    isPinned: false,
    isArchived: false
  });

  const [tagInput, setTagInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();
  const { success, error } = useToast();

  useEffect(() => {
    if (note) {
      setFormData({
        title: note.title,
        content: note.content,
        category: note.category,
        priority: note.priority,
        tags: [...note.tags],
        isPinned: note.isPinned,
        isArchived: note.isArchived
      });
    }
  }, [note]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note) return;

    try {
      await updateNoteMutation.mutateAsync({
        noteId: note.id,
        updates: formData
      });

      success('Note updated successfully!');
      onClose();
    } catch (err: any) {
      error('Failed to update note', err.message || 'Please try again');
    }
  };

  const handleDelete = async () => {
    if (!note) return;

    try {
      await deleteNoteMutation.mutateAsync(note.id);
      success('Note deleted successfully!');
      onClose();
      setShowDeleteConfirm(false);
    } catch (err: any) {
      error('Failed to delete note', err.message || 'Please try again');
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  if (!isOpen || !note) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Note</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {note.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Metadata */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Created: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {new Date(note.createdDate).toLocaleDateString()} by {note.createdBy}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Last Modified: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {new Date(note.lastModified).toLocaleDateString()} by {note.lastModifiedBy}
                  </span>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <StandardInput
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                />

                <div className="grid grid-cols-2 gap-4">
                  <StandardSelect
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as NoteCategory }))}
                    options={[
                      { value: 'general', label: 'General' },
                      { value: 'meeting', label: 'Meeting' },
                      { value: 'task', label: 'Task' },
                      { value: 'idea', label: 'Idea' },
                      { value: 'issue', label: 'Issue' },
                      { value: 'decision', label: 'Decision' }
                    ]}
                  />

                  <StandardSelect
                    label="Priority"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as NotePriority }))}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Note Content</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    HTML formatting supported: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;
                  </div>
                </div>
              </div>

              <StandardTextarea
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            {/* Tags */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tags</h3>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <StandardInput
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add tags (press Enter)"
                      leftIcon={<TagIcon className="w-4 h-4" />}
                    />
                  </div>
                  <StandardButton
                    type="button"
                    variant="primary"
                    onClick={addTag}
                  >
                    Add Tag
                  </StandardButton>
                </div>

                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-full flex items-center gap-2 text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-purple-900 dark:hover:text-purple-200"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Options</h3>

              <div className="space-y-2">
                <StandardCheckbox
                  label="Pin this note to the top"
                  checked={formData.isPinned}
                  onChange={(checked) => setFormData(prev => ({ ...prev, isPinned: checked }))}
                />

                <StandardCheckbox
                  label="Archive this note"
                  checked={formData.isArchived}
                  onChange={(checked) => setFormData(prev => ({ ...prev, isArchived: checked }))}
                />
              </div>
            </div>

            {/* Delete Note Section */}
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Deleting this note is permanent and cannot be undone.
              </p>
              {!showDeleteConfirm ? (
                <StandardButton
                  type="button"
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Note
                </StandardButton>
              ) : (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-900 dark:text-red-300 mb-1">Are you sure?</p>
                      <p className="text-sm text-red-800 dark:text-red-400">
                        This will permanently delete "{note.title}".
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <StandardButton
                      type="button"
                      variant="danger"
                      onClick={handleDelete}
                      disabled={deleteNoteMutation.isPending}
                      isLoading={deleteNoteMutation.isPending}
                    >
                      {deleteNoteMutation.isPending ? 'Deleting...' : 'Yes, Delete Note'}
                    </StandardButton>
                    <StandardButton
                      type="button"
                      variant="ghost"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </StandardButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={updateNoteMutation.isPending}
            isLoading={updateNoteMutation.isPending}
          >
            {updateNoteMutation.isPending ? 'Saving...' : 'Save Changes'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
